import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { findCategoryHierarchy } from "../utils/findCategoryHierarchy";

const ReadMoreList = ({ items, field, label, showMore, toggleShowMore }) => {
  if (!items || items.length === 0) return null;

  const maxItems = 3;
  const shouldShowReadMore = items.length > maxItems;
  const displayedItems = showMore[field] ? items : items.slice(0, maxItems);

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-gray-900">{label}</h2>
      <ul className="text-sm md:text-base text-gray-700 list-disc pl-5">
        {displayedItems.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
      {shouldShowReadMore && (
        <button
          onClick={() => toggleShowMore(field)}
          className="text-[#35A93E] font-medium text-sm hover:underline"
        >
          {showMore[field] ? "Show Less" : "Read More"}
        </button>
      )}
    </div>
  );
};

const VariationOptions = ({
  variations,
  selectedVariation,
  onSelectVariation,
}) => {
  if (!variations?.length) return null;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold text-gray-900">
        Available Options
      </h2>
      <div className="flex flex-wrap gap-2">
        {variations.map((variation, index) => (
          <button
            key={index}
            onClick={() => onSelectVariation(variation)}
            className={`px-4 py-2 text-sm font-medium rounded-md border-2 ${
              selectedVariation?._id === variation._id
                ? "border-[#35A93E] bg-[#35A93E] text-white"
                : "border-gray-300 bg-white text-gray-900 hover:bg-gray-100"
            }`}
          >
            Size {variation.size} - Color {variation.color}
          </button>
        ))}
      </div>
    </div>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { product, category, subCategory, subSubCategory, subSubSubCategory } =
    location.state || {};
  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);
  console.log(location.state);
  // Fetch category data
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${base_url}product/getCategory?page=1&limit=1000&sortingColumn=createdAt&sortingOrder=asc`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          },
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCategories(data?.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Find product and hierarchy
  const hierarchy = findCategoryHierarchy(categories, {}, id);
  const displayProduct =
    product ||
    categories
      ?.flatMap((cat) =>
        (cat.subCategories || []).flatMap((subCat) => [
          ...(subCat.products || []),
          ...(subCat.subSubCategories || []).flatMap((subSubCat) => [
            ...(subSubCat.products || []),
            ...(subSubCat.subSubSubCategories || []).flatMap(
              (subSubSubCat) => subSubSubCat.products || [],
            ),
          ]),
        ]),
      )
      .find((p) => p._id === id);

  // Update location.state with hierarchy if direct navigation
  useEffect(() => {
    if (displayProduct && !product && !loading) {
      navigate(`/product-details/${id}`, {
        state: {
          product: displayProduct,
          category: hierarchy.category,
          subCategory: hierarchy.subCategory,
          subSubCategory: hierarchy.subSubCategory,
          subSubSubCategory: hierarchy.subSubSubCategory,
        },
        replace: true,
      });
    }
  }, [displayProduct, product, loading, navigate, id, hierarchy]);

  // Redirect to /category if no valid product is found
  useEffect(() => {
    if (!displayProduct && !loading) {
      navigate("/", { replace: true });
    }
  }, [displayProduct, loading, navigate]);

  // State management
  const [selectedVariation, setSelectedVariation] = useState(
    displayProduct?.variations?.[0] || null,
  );
  const [selectedImage, setSelectedImage] = useState(
    displayProduct?.variations?.[0]?.images?.[0] ||
      displayProduct?.images?.[0] ||
      null,
  );
  const [showMore, setShowMore] = useState({
    benefits: false,
    features: false,
    instructionsForUse: false,
    batteryInfo: false,
    operatingConditions: false,
    packageContents: false,
    precautions: false,
  });

  const [token, setToken] = useState(() =>
    localStorage.getItem("user_website")
      ? JSON.parse(localStorage.getItem("user_website"))
      : null,
  );

  useEffect(() => {
    const handleStorageChange = () => {
      const user = localStorage.getItem("user_website");
      const newToken = user ? JSON.parse(user) : null;
      setToken(newToken);
    };

    window.addEventListener("storage", handleStorageChange);
    const intervalId = setInterval(() => {
      const user = localStorage.getItem("user_website");
      const newToken = user ? JSON.parse(user) : null;
      if (newToken?.token) {
        setToken(newToken);
        clearInterval(intervalId);
      }
    }, 500);

    handleStorageChange();
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
    };
  }, []);

  const mappedProduct = useMemo(
    () => ({
      _id: displayProduct?._id,
      productName: displayProduct?.productName,
      briefDescription: displayProduct?.briefDescription,
      benefits: displayProduct?.benefits,
      features: displayProduct?.features,
      instructionsForUse: displayProduct?.instructionsForUse,
      batteryInfo: displayProduct?.batteryInfo,
      operatingConditions: displayProduct?.operatingConditions,
      packageContents: displayProduct?.packageContents,
      precautions: displayProduct?.precautions,
      cost: {
        min:
          selectedVariation?.price?.amount ||
          displayProduct?.price?.amount ||
          displayProduct?.cost?.min,
        max: null,
      },
      image: selectedVariation?.images || displayProduct?.images,
      packageType: displayProduct?.packageType || "Standard",
      delivery: displayProduct?.delivery || "5-7 business days",
      quantity: selectedVariation?.quantity || displayProduct?.quantity || 1,
      size: selectedVariation?.size || displayProduct?.size || "N/A",
      rfidNfcCard: displayProduct?.rfidNfcCard || "Yes",
      activation: displayProduct?.activation || "Instant",
      material: displayProduct?.material || "N/A",
      weight: selectedVariation?.weight || "N/A",
      dimensions: selectedVariation?.dimensions || null,
      brandName: displayProduct?.brandName || "N/A",
      manufacturerName: displayProduct?.manufacturerName || "N/A",
      hsnCode: displayProduct?.hsnCode || "N/A",
      duration_days: displayProduct?.duration_days || null,
      powerRequirements: displayProduct?.powerRequirements || null,
      productType: displayProduct?.productType || "physical",
    }),
    [displayProduct, selectedVariation],
  );

  const handleVariationChange = useCallback(
    (variation) => {
      setSelectedVariation(variation);
      setSelectedImage(variation?.images?.[0] || displayProduct?.images?.[0]);
    },
    [displayProduct],
  );

  const toggleShowMore = useCallback((field) => {
    setShowMore((prev) => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const addToCart = useCallback(async () => {
    try {
      if (!token?.token) {
        toast.error("Please log in to add items to cart");
        return;
      }

      const isWalletUser = token?.data?.wallet?.referralWallet;
      const cartUrl = isWalletUser
        ? `${base_url}product/addtocartMember`
        : `${base_url}product/addtoCart`;

      const cartBody = {
        productId: displayProduct?._id,
        subCategoryId:
          subCategory || (subCategory || hierarchy.subCategory)?._id,
        categoryId: category || (category || hierarchy.category)?._id,
        subSubCategoryId: subSubCategory || (subSubCategory || hierarchy.subSubCategory)?._id,
        subSubSubCategoryId: subSubSubCategory || (subSubSubCategory || hierarchy.subSubSubCategory)
          ?._id,
        variationId: selectedVariation?._id,
        quantity: 1,
      };

      const response = await fetch(cartUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.token}`,
        },
        body: JSON.stringify(cartBody),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      if (!response.ok) {
        throw new Error(
          data?.message || `Failed to add to cart (status: ${response.status})`,
        );
      }
      if (data?.status === "success" || response.ok) {
        toast.success(data?.message || "Added to cart!");
        navigate("/cart")
      } else {
        throw new Error(data?.message || "Unexpected response from server");
      }
    } catch (error) {
      let errorMessage = "Failed to add to cart";
      if (error.message.includes("Failed to fetch")) {
        errorMessage = "Network error, please check your connection";
      } else if (error.message.includes("HTTP error")) {
        errorMessage = error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  }, [
    token,
    displayProduct,
    category,
    subCategory,
    subSubCategory,
    subSubSubCategory,
    selectedVariation,
    hierarchy,
  ]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!displayProduct) {
    return null; // Render nothing while redirecting
  }

  return (
    <div className="min-h-screen flex flex-col items-center p-4 sm:p-6 md:p-8 lg:p-10 my-20 font-sans">
      <div className="w-full max-w-7xl flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] bg-white rounded-lg shadow-md flex items-center justify-center overflow-hidden">
            <img
              src={selectedImage || mappedProduct.image?.[0]}
              alt={mappedProduct.productName}
              className="w-full h-full object-contain transition-transform duration-200 ease-in-out hover:scale-150"
              loading="lazy"
            />
          </div>
          {mappedProduct.image?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto py-2">
              {mappedProduct.image.map((img, index) => (
                <div
                  key={index}
                  className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-md flex items-center justify-center cursor-pointer border-2 ${
                    selectedImage === img
                      ? "border-[#35A93E]"
                      : "border-gray-300"
                  }`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={img}
                    alt={`${mappedProduct.productName} thumbnail ${index + 1}`}
                    className="w-full h-full object-contain rounded-md"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="w-full lg:w-1/2 flex flex-col gap-6 bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {mappedProduct.productName}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 whitespace-pre-line">
            {mappedProduct.briefDescription}
          </p>

          <div className="flex items-center gap-4">
            <span className="text-xl sm:text-2xl font-semibold text-gray-900">
              {getCurrencySymbol()}
              {convertPrice(mappedProduct.cost.min)}
            </span>
            {mappedProduct.cost.max && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 line-through">
                  ₹{mappedProduct.cost.max}
                </span>
              </div>
            )}
          </div>

          <VariationOptions
            variations={displayProduct?.variations}
            selectedVariation={selectedVariation}
            onSelectVariation={handleVariationChange}
          />

          <ReadMoreList
            items={mappedProduct.benefits}
            field="benefits"
            label="Product Benefits"
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />

          {mappedProduct.productType === "digital" && (
            <ReadMoreList
              items={mappedProduct.features}
              field="features"
              label="Features"
              showMore={showMore}
              toggleShowMore={toggleShowMore}
            />
          )}

          <ReadMoreList
            items={mappedProduct.instructionsForUse}
            field="instructionsForUse"
            label="Instructions for Use"
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />

          {mappedProduct.productType === "physical" && (
            <>
              <ReadMoreList
                items={mappedProduct.batteryInfo}
                field="batteryInfo"
                label="Battery Information"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
              <ReadMoreList
                items={mappedProduct.operatingConditions}
                field="operatingConditions"
                label="Operating Conditions"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
              <ReadMoreList
                items={mappedProduct.packageContents}
                field="packageContents"
                label="Package Contents"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
              <ReadMoreList
                items={mappedProduct.precautions}
                field="precautions"
                label="Precautions"
                showMore={showMore}
                toggleShowMore={toggleShowMore}
              />
            </>
          )}

          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              Additional Details
            </h2>
            <div className="text-sm sm:text-base text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <DetailItem label="Brand" value={mappedProduct.brandName} />
              <DetailItem
                label="Manufacturer"
                value={mappedProduct.manufacturerName}
              />
              <DetailItem label="HSN Code" value={mappedProduct.hsnCode} />
              <DetailItem
                label="Package Type"
                value={mappedProduct.packageType}
              />
              <DetailItem
                label="Delivery Time"
                value={mappedProduct.delivery}
              />
              {/* <DetailItem label="Quantity" value={`${mappedProduct.quantity} units`} /> */}
              <DetailItem label="Size" value={mappedProduct.size} />
              {mappedProduct.material &&
                mappedProduct.productType === "physical" && (
                  <DetailItem label="Material" value={mappedProduct.material} />
                )}
              {mappedProduct.weight &&
                mappedProduct.productType === "physical" && (
                  <DetailItem
                    label="Weight"
                    value={`${mappedProduct.weight} kg`}
                  />
                )}
              {mappedProduct.dimensions &&
                mappedProduct.productType === "physical" && (
                  <DetailItem
                    label="Dimensions"
                    value={`${mappedProduct.dimensions.length} x ${mappedProduct.dimensions.width} x ${mappedProduct.dimensions.height} cm`}
                  />
                )}
              <DetailItem
                label="RFID/NFC Card"
                value={mappedProduct.rfidNfcCard}
              />
              <DetailItem label="Activation" value={mappedProduct.activation} />
              {mappedProduct.duration_days &&
                mappedProduct.productType === "digital" && (
                  <DetailItem
                    label="Duration"
                    value={`${mappedProduct.duration_days} days`}
                  />
                )}
              {mappedProduct.powerRequirements &&
                mappedProduct.powerRequirements.length > 0 &&
                mappedProduct.productType === "physical" && (
                  <DetailItem
                    label="Power Requirements"
                    value={mappedProduct.powerRequirements.join(", ")}
                  />
                )}
            </div>
          </div>

          <div className="w-full sm:w-80">
            <button
              onClick={addToCart}
              className="w-full h-12 bg-[#35A93E] text-white font-semibold text-base rounded-md hover:bg-[#2e8b35] transition flex items-center justify-center gap-2 shadow-md"
            >
              <FaShoppingCart /> Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <p>
    <span className="font-semibold text-gray-900">{label}:</span> {value}
  </p>
);

export default ProductDetails;
